import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BriefingFlowTag } from '../../components/BriefingFlowTag';
import { useWindowSize } from '../../shared/useWindowSize';
import { Branch, Step } from '../../types/misc';

type TagName = 'base' | 'colour' | 'fragrance';

type BriefingFlowTagControllerProps = {
  step: Step;
  branch: Branch;
  isEmployeeUser?: boolean;
};

export const BriefingFlowTagController: FC<BriefingFlowTagControllerProps> = ({
  step,
  branch,
  isEmployeeUser,
}) => {
  const [windowSize] = useWindowSize();
  const { t } = useTranslation();
  const [segmentName, setSegmentName] = useState<TagName>(
    t('labels:briefingFlowTag:base') as TagName,
  );
  const [tagHeight, setTagHeight] = useState(60);
  const [tagColour, setTagColour] = useState('');
  const [leftPosition, setLeftPosition] = useState(0);
  const [topPosition, setTopPosition] = useState(isEmployeeUser ? 40 : 0);
  const [showTagOnStep, setShowTagOnStep] = useState(true);

  // Render the screen position of the tag
  useEffect(() => {
    if (windowSize > 1024) {
      setLeftPosition(12);
      setTopPosition(isEmployeeUser ? 40 : 0);
    } else {
      setLeftPosition(0);
      setTopPosition(isEmployeeUser ? 72 : 14);
    }
  }, [windowSize, isEmployeeUser]);

  // Render the correct tag name dependant on the step and the branch
  useEffect(() => {
    switch (step) {
      case 'fragrance':
        setSegmentName(t('labels:briefingFlowTag:fragrance') as TagName);
        break;

      case 'colour':
        setSegmentName(t('labels:briefingFlowTag:colour') as TagName);
        break;

      case 'application':
      case 'requirements':
      case 'formulation-attributes':
      default:
        setSegmentName(t('labels:briefingFlowTag:base') as TagName);
        break;
    }

    // Hide tag on certain screens
    if (
      step === 'review' ||
      step === 'modification-type' ||
      step === 'modification-issue'
    ) {
      setShowTagOnStep(false);
    } else {
      setShowTagOnStep(true);
    }
  }, [step, branch, t]);

  // Render the correct tag colour and height dependant on tag name
  useEffect(() => {
    switch (segmentName) {
      case 'colour':
        setTagColour('#C3A0C9');
        setTagHeight(89);
        break;
      case 'fragrance':
        setTagColour('#11928C');
        setTagHeight(111);
        break;
      case 'base':
      default:
        setTagColour('#F6892A');
        setTagHeight(60);
        break;
    }
  }, [segmentName]);

  return (
    <>
      {showTagOnStep && (
        <BriefingFlowTag
          colour={tagColour}
          height={tagHeight}
          name={segmentName}
          topPosition={topPosition}
          leftPosition={leftPosition}
        />
      )}
    </>
  );
};
